<!--
  * Path: packages/frontend/src/App.vue
  * Task: Application root component. Delegates layout rendering to NavigationLayout and mounts the router view.
  * Update: CRITICAL FIX. Re-introduced a targeted `:key="route.name"` to fix frozen layouts when switching views via sidebar, while keeping deep-link performance intact.
-->
<template>
  <NavigationLayout>
    <router-view v-slot="{ Component, route }">
      <!-- Transition applied globally. -->
      <!-- CRITICAL FIX: We use route.name as the key instead of route.fullPath! -->
      <!-- This forces Vue to properly unmount/remount components when switching BETWEEN views (e.g. Web Map to Sequence Map), -->
      <!-- but STILL prevents massive remounts during simple deep-link interactions (?entity=123) within the SAME view. -->
      <transition name="fade" mode="out-in">
        <component :is="Component" :key="route.name" />
      </transition>
    </router-view>
  </NavigationLayout>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { useTheme } from 'vuetify';
import { useAuthStore } from '@/store/auth';
import NavigationLayout from '@/core/components/common/NavigationLayout.vue';

const authStore = useAuthStore();
const theme = useTheme();

// Applies the current visibility mode to Vuetify's theme engine
const applyTheme = (mode: string) => {
  if (mode === 'auto') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    theme.global.name.value = prefersDark ? 'dark' : 'light';
  } else {
    theme.global.name.value = mode;
  }
};

onMounted(async () => {
  // Check session or initialize necessary global data on initial load
  try {
    await authStore.checkSession();
  } catch (error) {
    console.error('Failed to initialize session on app mount:', error);
  }

  // Apply theme initially
  applyTheme(authStore.visibilityMode);

  // Listen to system theme changes (for 'auto' mode)
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (authStore.visibilityMode === 'auto') {
      applyTheme('auto');
    }
  });
});

// Watch for manual theme changes globally
watch(() => authStore.visibilityMode, (newMode) => {
  applyTheme(newMode);
});
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden; /* Prevent body scroll, Vuetify handles scrolling internally */
}

/* Fade transition classes for router view */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
